<template>
  <div id="day-review">
    <div class="container-fluid pt-4 px-4">
      <v-alert type="info" variant="tonal" density="comfortable" class="mb-4" rounded="lg">
        Pick any trading day to see the real 1-minute candles for whatever stock(s) the live system
        actually placed an order for that day (entry/stop/target/exit), plus a backtest comparison
        (single-stage vs multi-stage exit) overlaid on the same chart. Dates with no real live order
        (e.g. any day the live cron wasn't running) fall back to showing the offline backtest's pick instead.
      </v-alert>

      <v-card flat rounded="lg" class="gap-backtest-card mb-4">
        <v-card-text class="px-5 py-4">
          <div class="d-flex flex-wrap align-center ga-4">
            <div class="mode-toggle">
              <v-btn
                :variant="mode === 'live' ? 'flat' : 'tonal'"
                :color="mode === 'live' ? 'primary' : undefined"
                rounded="lg" size="small" class="mode-btn"
                @click="switchToLive"
              >
                Live
              </v-btn>
              <v-btn
                :variant="mode === 'history' ? 'flat' : 'tonal'"
                :color="mode === 'history' ? 'primary' : undefined"
                rounded="lg" size="small" class="mode-btn"
                @click="switchToHistory"
              >
                History
              </v-btn>
              <v-btn
                :variant="mode === 'decision-log' ? 'flat' : 'tonal'"
                :color="mode === 'decision-log' ? 'primary' : undefined"
                rounded="lg" size="small" class="mode-btn"
                @click="switchToDecisionLog"
              >
                Decision Log
              </v-btn>
              <v-btn
                :variant="mode === 'divergence' ? 'flat' : 'tonal'"
                :color="mode === 'divergence' ? 'primary' : undefined"
                rounded="lg" size="small" class="mode-btn"
                @click="switchToDivergence"
              >
                Divergence
              </v-btn>
              <v-btn
                :variant="mode === 'analytics' ? 'flat' : 'tonal'"
                :color="mode === 'analytics' ? 'primary' : undefined"
                rounded="lg" size="small" class="mode-btn"
                @click="switchToAnalytics"
              >
                Analytics
              </v-btn>
              <v-btn
                :variant="mode === 'price-integrity' ? 'flat' : 'tonal'"
                :color="mode === 'price-integrity' ? 'primary' : undefined"
                rounded="lg" size="small" class="mode-btn"
                @click="switchToPriceIntegrity"
              >
                Price Integrity
              </v-btn>
            </div>

            <template v-if="mode === 'live'">
              <span class="text-body-2 text-medium-emphasis">Today — {{ date }}</span>
              <v-btn variant="text" size="small" :loading="!loaded" @click="load">
                <font-awesome-icon icon="fa-solid fa-rotate-right" class="mr-1" /> Refresh
              </v-btn>
              <span class="text-caption text-medium-emphasis">Auto-refreshes every 20s</span>
            </template>
            <template v-else-if="mode === 'history'">
              <v-text-field
                v-model="date" type="date" label="Date" variant="outlined" density="comfortable"
                hide-details style="max-width: 220px"
              />
            </template>
          </div>

          <template v-if="mode === 'decision-log'">
            <v-divider class="my-4" />
            <div class="text-body-2 text-medium-emphasis mb-3">
              Every stock the live cron evaluated in this range — traded or not, and why. A row with no
              real order shows exactly which gate it didn't clear (see the Reason column); a real entry
              also shows why buy vs. sell in the Action column.
            </div>
            <div class="d-flex flex-wrap align-end ga-3">
              <DateRangeFilter v-model="decisionLogRange" label="Date range" style="min-width: 320px" />
              <v-text-field
                v-model="decisionLogCode" label="Stock code (optional)" variant="outlined" density="comfortable"
                hide-details style="max-width: 200px" clearable
              />
              <v-btn color="primary" variant="tonal" rounded="lg" :loading="!decisionLogLoaded" @click="loadDecisionLog">
                Load
              </v-btn>
              <v-btn v-if="decisionLogRows.length" variant="text" size="small" @click="downloadDecisionLogCsv">
                <font-awesome-icon icon="fa-solid fa-download" class="mr-1" /> Download CSV
              </v-btn>
            </div>

            <v-alert v-if="decisionLogLoaded && !decisionLogRows.length" type="info" variant="tonal" density="comfortable" class="mt-4" rounded="lg">
              No candidates evaluated in this range yet (or the live cron hasn't run over it).
            </v-alert>

            <div v-if="decisionLogRows.length" class="table-scroll mt-4">
              <v-table density="compact" class="custom-data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Stock</th>
                    <th>Status</th>
                    <th>Reason</th>
                    <th>Action</th>
                    <th>Gap %</th>
                    <th>Confidence</th>
                    <th>Historical win rate</th>
                    <th>Rank</th>
                    <th>Exit / P&amp;L</th>
                    <th>Detail</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in decisionLogRows" :key="`${row.trade_date}-${row.code}`">
                    <td>{{ row.trade_date }}</td>
                    <td>{{ row.code }}</td>
                    <td><v-chip size="small" variant="tonal" :color="statusColor(row.status)">{{ row.status }}</v-chip></td>
                    <td>{{ decisionReasonLabel(row) }}</td>
                    <td>{{ actionLabel(row) }}</td>
                    <td>{{ row.gap_pct !== null && row.gap_pct !== undefined ? `${Number(row.gap_pct).toFixed(2)}%` : '—' }}</td>
                    <td>{{ formatPct(row.today_confidence_pct) }}</td>
                    <td>
                      <template v-if="row.historical_win_rate_pct !== null && row.historical_win_rate_pct !== undefined">
                        {{ formatPct(row.historical_win_rate_pct) }} (Wilson {{ formatPct(row.wilson_lower_bound_pct) }})
                      </template>
                      <template v-else>—</template>
                    </td>
                    <td>{{ row.rank_position ?? '—' }}</td>
                    <td>
                      <template v-if="row.exit_reason">{{ row.exit_reason }} · {{ formatMoney(row.pnl_rs) }}</template>
                      <template v-else>—</template>
                    </td>
                    <td class="decision-detail-cell">{{ decisionDetailText(row) }}</td>
                    <td>
                      <v-btn
                        v-if="row.decision_code === 'entered'"
                        variant="text" size="small" density="compact"
                        @click="openTradeExplorer(row)"
                      >
                        Explore
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </div>
          </template>

          <template v-if="mode === 'divergence'">
            <v-divider class="my-4" />
            <div class="text-body-2 text-medium-emphasis mb-3">
              For every real, entered trade in this range, compares the win rate the backtest predicted at
              entry time against what actually happened — a widening gap over time is an early signal of
              slippage, stale historical data, or a regime change. Read-only: never feeds back into
              selection/sizing/exit logic.
            </div>
            <div class="d-flex flex-wrap align-end ga-3">
              <DateRangeFilter v-model="divergenceRange" label="Date range" style="min-width: 320px" />
              <v-btn color="primary" variant="tonal" rounded="lg" :loading="!divergenceLoaded" @click="loadDivergence">
                Load
              </v-btn>
            </div>

            <v-alert v-if="divergenceLoaded && !divergenceWeekly.length" type="info" variant="tonal" density="comfortable" class="mt-4" rounded="lg">
              No closed real trades in this range yet.
            </v-alert>

            <div v-if="divergenceSummary" class="text-body-2 mt-4">
              <span class="text-medium-emphasis">Overall ({{ divergenceSummary.trades }} trade{{ divergenceSummary.trades === 1 ? '' : 's' }}):</span>
              <span class="ml-2">Actual win rate <strong>{{ formatPct(divergenceSummary.actualWinRatePct) }}</strong></span>
              <span class="ml-2">· Predicted <strong>{{ formatPct(divergenceSummary.predictedWinRatePct) }}</strong></span>
              <span class="ml-2">· Predicted Wilson LB <strong>{{ formatPct(divergenceSummary.predictedWilsonLowerBoundPct) }}</strong></span>
              <span class="ml-2">· Delta
                <strong :class="deltaClass(divergenceSummary.deltaVsPredictedPct)">{{ formatSignedPct(divergenceSummary.deltaVsPredictedPct) }}</strong>
              </span>
            </div>

            <div v-if="divergenceWeekly.length" class="table-scroll mt-4">
              <v-table density="compact" class="custom-data-table">
                <thead>
                  <tr>
                    <th>Week of</th>
                    <th>Trades</th>
                    <th>Won</th>
                    <th>Lost</th>
                    <th>Closed flat</th>
                    <th>Actual win rate</th>
                    <th>Predicted win rate</th>
                    <th>Predicted Wilson LB</th>
                    <th>Delta vs. predicted</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="week in divergenceWeekly" :key="week.weekStart">
                    <td>{{ week.weekStart }}</td>
                    <td>{{ week.trades }}</td>
                    <td>{{ week.won }}</td>
                    <td>{{ week.lost }}</td>
                    <td>{{ week.closedFlat }}</td>
                    <td>{{ formatPct(week.actualWinRatePct) }}</td>
                    <td>{{ formatPct(week.predictedWinRatePct) }}</td>
                    <td>{{ formatPct(week.predictedWilsonLowerBoundPct) }}</td>
                    <td :class="deltaClass(week.deltaVsPredictedPct)">{{ formatSignedPct(week.deltaVsPredictedPct) }}</td>
                  </tr>
                </tbody>
              </v-table>
            </div>
          </template>

          <template v-if="mode === 'analytics'">
            <v-divider class="my-4" />
            <div class="text-body-2 text-medium-emphasis mb-3">
              Win rate, profit factor, expectancy, drawdown, and hold time across real closed orders
              (open trades excluded — their P&amp;L is still moving). The confidence badge on each tile
              reflects how many closed trades it's based on — treat "Experimental"/"Limited data" numbers
              as directional, not decisive.
            </div>
            <div class="d-flex flex-wrap align-end ga-3">
              <DateRangeFilter v-model="analyticsRange" label="Date range" style="min-width: 320px" />
              <v-btn color="primary" variant="tonal" rounded="lg" :loading="!analyticsLoaded" @click="loadAnalytics">
                Load
              </v-btn>
            </div>

            <v-alert v-if="analyticsLoaded && analyticsData && !analyticsData.closedCount" type="info" variant="tonal" density="comfortable" class="mt-4" rounded="lg">
              No closed real trades in this range yet.
            </v-alert>

            <template v-if="analyticsData && analyticsData.closedCount">
              <TradeStatsTiles :stats="analyticsData" :show-formula-break-caveat="showsFormulaBreakCaveat" class="mt-2" />

              <v-alert v-if="showsFormulaBreakCaveat" type="warning" variant="tonal" density="comfortable" class="mt-2" rounded="lg">
                This range spans {{ analyticsData.formulaBreakDate }}, when the transaction-cost formula changed
                (brokerage 0.1%/₹5-floor → 0.03%/₹20-cap). P&amp;L before and after that date isn't directly
                comparable — a jump in the chart below around that date is a bookkeeping correction, not a
                strategy change.
              </v-alert>

              <v-row class="mt-2">
                <v-col cols="12" md="6">
                  <EquityCurveChart :daily="analyticsData.daily" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-card flat rounded="lg" class="gap-backtest-card">
                    <v-card-title class="px-5 py-4 d-flex align-center justify-space-between flex-wrap ga-2">
                      <span class="text-h6 font-weight-medium">P&amp;L by period</span>
                      <v-btn-toggle v-model="analyticsBucket" density="compact" mandatory rounded="lg" color="primary" variant="outlined">
                        <v-btn value="daily" size="small">Day</v-btn>
                        <v-btn value="weekly" size="small">Week</v-btn>
                        <v-btn value="monthly" size="small">Month</v-btn>
                      </v-btn-toggle>
                    </v-card-title>
                    <v-card-text class="px-5 pb-5 chart-box">
                      <Bar :data="bucketChartData" :options="bucketChartOptions" />
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <v-row class="mt-2">
                <v-col cols="12" md="6">
                  <v-card flat rounded="lg" class="gap-backtest-card">
                    <v-card-title class="px-5 py-4">
                      <span class="text-h6 font-weight-medium">By gap %</span>
                    </v-card-title>
                    <v-card-text class="px-5 pb-5">
                      <v-table density="compact" class="custom-data-table">
                        <thead>
                          <tr><th>Gap</th><th>Trades</th><th>Win rate</th><th>P&amp;L</th><th></th></tr>
                        </thead>
                        <tbody>
                          <tr v-for="row in analyticsData.byGapPct" :key="row.label">
                            <td>{{ row.label }}</td>
                            <td>{{ row.tradeCount }}</td>
                            <td>{{ formatPct(row.winRatePct) }}</td>
                            <td :class="row.pnlRs >= 0 ? 'text-success' : 'text-error'">{{ formatMoney(row.pnlRs) }}</td>
                            <td><v-chip size="x-small" variant="tonal" :color="confidenceColor(row.confidenceTier)">{{ confidenceLabel(row.confidenceTier) }}</v-chip></td>
                          </tr>
                        </tbody>
                      </v-table>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card flat rounded="lg" class="gap-backtest-card">
                    <v-card-title class="px-5 py-4">
                      <span class="text-h6 font-weight-medium">By weekday</span>
                    </v-card-title>
                    <v-card-text class="px-5 pb-5">
                      <v-table density="compact" class="custom-data-table">
                        <thead>
                          <tr><th>Day</th><th>Trades</th><th>Win rate</th><th>P&amp;L</th><th></th></tr>
                        </thead>
                        <tbody>
                          <tr v-for="row in analyticsData.byWeekday" :key="row.label">
                            <td>{{ row.label }}</td>
                            <td>{{ row.tradeCount }}</td>
                            <td>{{ formatPct(row.winRatePct) }}</td>
                            <td :class="row.pnlRs >= 0 ? 'text-success' : 'text-error'">{{ formatMoney(row.pnlRs) }}</td>
                            <td><v-chip size="x-small" variant="tonal" :color="confidenceColor(row.confidenceTier)">{{ confidenceLabel(row.confidenceTier) }}</v-chip></td>
                          </tr>
                        </tbody>
                      </v-table>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <StockRankingTable :by-stock="analyticsData.byStock" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-card flat rounded="lg" class="gap-backtest-card">
                    <v-card-title class="px-5 py-4">
                      <span class="text-h6 font-weight-medium">By confidence score</span>
                    </v-card-title>
                    <v-card-text class="px-5 pb-5">
                      <div class="text-caption text-medium-emphasis mb-2">
                        Does today_confidence_pct (computed at selection time) actually predict outcomes? A rising win
                        rate from bottom to top row is the sign it does; a flat or inverted trend means the ranking
                        formula needs another look.
                      </div>
                      <v-table density="compact" class="custom-data-table">
                        <thead>
                          <tr><th>Confidence</th><th>Trades</th><th>Win rate</th><th>P&amp;L</th><th></th></tr>
                        </thead>
                        <tbody>
                          <tr v-for="row in analyticsData.byConfidence" :key="row.label">
                            <td>{{ row.label }}</td>
                            <td>{{ row.tradeCount }}</td>
                            <td>{{ formatPct(row.winRatePct) }}</td>
                            <td :class="row.pnlRs >= 0 ? 'text-success' : 'text-error'">{{ formatMoney(row.pnlRs) }}</td>
                            <td><v-chip size="x-small" variant="tonal" :color="confidenceColor(row.confidenceTier)">{{ confidenceLabel(row.confidenceTier) }}</v-chip></td>
                          </tr>
                        </tbody>
                      </v-table>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card flat rounded="lg" class="gap-backtest-card">
                    <v-card-title class="px-5 py-4">
                      <span class="text-h6 font-weight-medium">By exit reason</span>
                    </v-card-title>
                    <v-card-text class="px-5 pb-5">
                      <div class="text-caption text-medium-emphasis mb-2">
                        Sorted by net P&amp;L, worst first — the reason at the top is costing the most, not just
                        firing the most often.
                      </div>
                      <v-table density="compact" class="custom-data-table">
                        <thead>
                          <tr><th>Exit reason</th><th>Trades</th><th>Win rate</th><th>P&amp;L</th><th></th></tr>
                        </thead>
                        <tbody>
                          <tr v-for="row in analyticsData.byExitReason" :key="row.label">
                            <td>{{ row.label }}</td>
                            <td>{{ row.tradeCount }}</td>
                            <td>{{ formatPct(row.winRatePct) }}</td>
                            <td :class="row.pnlRs >= 0 ? 'text-success' : 'text-error'">{{ formatMoney(row.pnlRs) }}</td>
                            <td><v-chip size="x-small" variant="tonal" :color="confidenceColor(row.confidenceTier)">{{ confidenceLabel(row.confidenceTier) }}</v-chip></td>
                          </tr>
                        </tbody>
                      </v-table>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card flat rounded="lg" class="gap-backtest-card">
                    <v-card-title class="px-5 py-4">
                      <span class="text-h6 font-weight-medium">By hold time</span>
                    </v-card-title>
                    <v-card-text class="px-5 pb-5">
                      <v-table density="compact" class="custom-data-table">
                        <thead>
                          <tr><th>Hold time</th><th>Trades</th><th>Win rate</th><th>P&amp;L</th><th></th></tr>
                        </thead>
                        <tbody>
                          <tr v-for="row in analyticsData.byHoldTime" :key="row.label">
                            <td>{{ row.label }}</td>
                            <td>{{ row.tradeCount }}</td>
                            <td>{{ formatPct(row.winRatePct) }}</td>
                            <td :class="row.pnlRs >= 0 ? 'text-success' : 'text-error'">{{ formatMoney(row.pnlRs) }}</td>
                            <td><v-chip size="x-small" variant="tonal" :color="confidenceColor(row.confidenceTier)">{{ confidenceLabel(row.confidenceTier) }}</v-chip></td>
                          </tr>
                        </tbody>
                      </v-table>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </template>
          </template>

          <template v-if="mode === 'price-integrity'">
            <v-divider class="my-4" />
            <div class="text-body-2 text-medium-emphasis mb-3">
              Cross-checks every real trade's entry/stop/target against the system's own real intraday
              candles (a separate data source from the live quote feed) — was the recorded entry price
              actually achievable? Catches a bad/stale quote print before it's mistaken for real trading
              performance. Read-only: never changes any trade's recorded numbers.
            </div>
            <div class="d-flex flex-wrap align-end ga-3">
              <DateRangeFilter v-model="priceIntegrityRange" label="Date range" style="min-width: 320px" />
              <v-text-field
                v-model="priceIntegrityCode" label="Stock code (optional)" variant="outlined" density="comfortable"
                hide-details style="max-width: 200px" clearable
              />
              <v-btn color="primary" variant="tonal" rounded="lg" :loading="!priceIntegrityLoaded" @click="loadPriceIntegrity">
                Load
              </v-btn>
              <v-btn
                v-if="priceIntegrityData?.flagged?.length" variant="text" size="small" @click="downloadPriceIntegrityCsv"
              >
                <font-awesome-icon icon="fa-solid fa-download" class="mr-1" /> Download CSV
              </v-btn>
            </div>

            <v-alert
              v-if="priceIntegrityLoaded && priceIntegrityData && !priceIntegrityData.summary.totalRealTrades"
              type="info" variant="tonal" density="comfortable" class="mt-4" rounded="lg"
            >
              No real trades in this range.
            </v-alert>

            <div v-if="priceIntegrityData?.summary?.totalRealTrades" class="text-body-2 mt-4">
              <span class="text-medium-emphasis">Real trades checked:</span>
              <span class="ml-2"><strong>{{ priceIntegrityData.summary.totalRealTrades }}</strong></span>
              <span class="ml-3 text-medium-emphasis">
                · Data unavailable <strong>{{ priceIntegrityData.summary.dataUnavailable }}</strong> (insufficient data to audit confidently — not an integrity finding)
              </span>
              <span class="ml-3 text-medium-emphasis">· Audited <strong>{{ priceIntegrityData.summary.audited }}</strong></span>
              <span class="ml-3">
                · Flagged
                <strong :class="priceIntegrityData.summary.flagged ? 'text-error' : 'text-success'">{{ priceIntegrityData.summary.flagged }}</strong>
                <template v-if="priceIntegrityData.summary.flagged">
                  ({{ priceIntegrityData.summary.confirmedBadPrint }} confirmed bad print, {{ priceIntegrityData.summary.suspectMismatch }} suspect)
                </template>
              </span>
              <span v-if="priceIntegrityData.summary.flagged" class="ml-3 text-medium-emphasis">
                · P&amp;L from flagged trades <strong class="text-medium-emphasis">{{ formatMoney(priceIntegrityData.summary.flaggedPnlImpactRs) }}</strong>
              </span>
            </div>

            <div v-if="priceIntegrityByStockRows.length" class="table-scroll mt-3">
              <v-table density="compact" class="custom-data-table">
                <thead>
                  <tr>
                    <th>Stock</th>
                    <th>Checked</th>
                    <th>Data unavailable</th>
                    <th>Audited</th>
                    <th>Flagged</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in priceIntegrityByStockRows" :key="row.code">
                    <td>{{ row.code }}</td>
                    <td>{{ row.checked }}</td>
                    <td class="text-medium-emphasis">{{ row.dataUnavailable }}</td>
                    <td>{{ row.audited }}</td>
                    <td :class="row.flagged ? 'text-error' : 'text-success'">{{ row.flagged }}</td>
                  </tr>
                </tbody>
              </v-table>
            </div>

            <v-alert
              v-if="priceIntegrityLoaded && priceIntegrityData?.summary?.audited && !priceIntegrityData.flagged.length"
              type="success" variant="tonal" density="comfortable" class="mt-4" rounded="lg"
            >
              No price-integrity issues found in this range.
            </v-alert>

            <div v-if="priceIntegrityData?.flagged?.length" class="table-scroll mt-4">
              <v-table density="compact" class="custom-data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Stock</th>
                    <th>Severity</th>
                    <th>Entry</th>
                    <th>Verified day range</th>
                    <th>Detail</th>
                    <th>Status / P&amp;L</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in priceIntegrityData.flagged" :key="`${row.trade_date}-${row.code}`">
                    <td>{{ row.trade_date }}</td>
                    <td>{{ row.code }}</td>
                    <td>
                      <div class="d-flex flex-column ga-1">
                        <v-chip size="small" variant="tonal" :color="priceIntegritySeverityColor(row.severity)">
                          {{ row.severity === 'CONFIRMED_BAD_PRINT' ? 'Confirmed price mismatch' : 'Suspect mismatch' }}
                        </v-chip>
                        <v-chip
                          v-if="row.confidence" size="x-small" variant="outlined"
                          :color="priceIntegrityConfidenceColor(row.confidence)"
                        >
                          {{ priceIntegrityConfidenceLabel(row.confidence) }}
                        </v-chip>
                      </div>
                    </td>
                    <td>
                      {{ $helper.formatPrice(row.entry_price) }}
                    </td>
                    <td>
                      {{ $helper.formatPrice(row.realDayLow) }} – {{ $helper.formatPrice(row.realDayHigh) }}
                      <div v-if="row.dayRangeSource === 'combined'" class="text-caption text-medium-emphasis">✓ StockHistory-corroborated</div>
                    </td>
                    <td class="decision-detail-cell">{{ priceIntegrityDetailText(row) }}</td>
                    <td>
                      <v-chip size="small" variant="tonal" :color="statusColor(row.status)">{{ row.status }}</v-chip>
                      <span class="ml-1">{{ formatMoney(row.pnl_rs) }}</span>
                    </td>
                    <td>
                      <v-btn
                        v-if="row.paper_trade_id" variant="text" size="small" density="compact"
                        @click="openTradeExplorer(row)"
                      >
                        Explore
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </div>

            <div v-if="priceIntegrityData?.skipped?.length" class="text-caption text-medium-emphasis mt-3">
              Skipped ({{ priceIntegrityData.skipped.length }}, insufficient data to audit confidently):
              <span v-for="(s, i) in priceIntegrityData.skipped" :key="`${s.trade_date}-${s.code}`">{{ i ? ', ' : ' ' }}{{ s.code }} {{ s.trade_date }} ({{ priceIntegritySkipReasonText(s) }})</span>
            </div>
            <div v-if="priceIntegrityData?.staleOpenWarnings?.length" class="text-caption text-error mt-1">
              Still open past trade date (EOD square-off may not have run):
              <span v-for="(w, i) in priceIntegrityData.staleOpenWarnings" :key="`${w.trade_date}-${w.code}`">{{ i ? ', ' : ' ' }}{{ w.code }} {{ w.trade_date }}</span>
            </div>
          </template>

          <template v-if="mode === 'history'">
            <v-divider class="my-4" />
            <div class="d-flex align-center flex-wrap ga-3 mb-3">
              <v-btn-toggle v-model="rangeMode" density="compact" mandatory rounded="lg" color="primary" variant="outlined">
                <v-btn value="derived" size="small">Derived selection</v-btn>
                <v-btn value="replay" size="small">Live-replay (real trades)</v-btn>
              </v-btn-toggle>
              <span class="text-body-2 text-medium-emphasis">
                <template v-if="rangeMode === 'derived'">
                  Backfill a whole range at once: re-derives which stock(s) would be picked each day, same pipeline as
                  picking one date, across the full watchlist — the date field above still controls which single day
                  is shown.
                </template>
                <template v-else>
                  Skips re-deriving which stock(s) would be picked. Uses whichever stock(s) the live system actually
                  entered a real order on each day, and re-simulates only what happens after that real entry under
                  current settings.
                </template>
              </span>
            </div>
            <div class="d-flex flex-wrap align-center ga-3">
              <v-text-field
                v-model="rangeFrom" type="date" label="From" variant="outlined" density="comfortable"
                hide-details style="max-width: 200px"
              />
              <v-text-field
                v-model="rangeTo" type="date" label="To" variant="outlined" density="comfortable"
                hide-details style="max-width: 200px"
              />
              <v-btn color="primary" variant="tonal" rounded="lg" :loading="runningRange" @click="runRange">
                {{ rangeMode === 'replay' ? 'Replay real trades for this range' : 'Run backtest for this range' }}
              </v-btn>
            </div>
            <v-alert v-if="rangeMessage" :type="rangeError ? 'error' : 'success'" variant="tonal" density="comfortable" class="mt-3" rounded="lg">
              {{ rangeMessage }}
            </v-alert>
            <v-alert
              v-if="rangeSummary && rangeSummary.mode === 'replay' && rangeSummary.tradeCount === 0"
              type="info" variant="tonal" density="comfortable" class="mt-3" rounded="lg"
            >
              No real trades in this range — the live system didn't enter any order between {{ rangeSummary.fromDate }}
              and {{ rangeSummary.toDate }}. Switch to "Derived selection" to see what the offline engine would have
              picked instead.
            </v-alert>
            <!-- Reward:risk gate (2026-08-09): candidates that never got
                 simulated at all because today's target-vs-stop distance
                 didn't clear min_reward_risk_ratio. Kept out of `trades`
                 entirely (folding them in would corrupt the P&L sums), so
                 this is a separate, compact list. Derived-selection only --
                 Live-Replay can't block an entry that already happened, it
                 flags it per-row in the table below instead. -->
            <v-alert
              v-if="rangeSummary && rangeSummary.gatedByRewardRisk && rangeSummary.gatedByRewardRisk.length"
              type="warning" variant="tonal" density="comfortable" class="mt-3" rounded="lg"
            >
              <strong>Blocked by reward:risk gate ({{ rangeSummary.gatedByRewardRisk.length }}):</strong>
              <div v-for="g in rangeSummary.gatedByRewardRisk" :key="`${g.date}-${g.code}`" class="text-caption">
                {{ g.date }} — {{ g.code }}: {{ g.rewardRiskRatio.toFixed(2) }} : 1 (needs ≥ {{ g.minRewardRiskRatio }} : 1)
              </div>
            </v-alert>
            <div v-if="rangeSummary" class="text-body-2 mt-2">
              <span class="text-medium-emphasis">Range P&amp;L ({{ rangeSummary.fromDate }} – {{ rangeSummary.toDate }}, {{ rangeSummary.tradeCount }} trade{{ rangeSummary.tradeCount === 1 ? '' : 's' }}{{ rangeSummary.mode === 'replay' ? ' (real)' : '' }}):</span>
              <span class="ml-2">
                Estimated ₹ — Single
                <strong :class="rangeSummary.estimatedSinglePnlRs >= 0 ? 'text-success' : 'text-error'">{{ formatMoney(rangeSummary.estimatedSinglePnlRs) }}</strong>
                · Multi
                <strong :class="rangeSummary.estimatedMultiPnlRs >= 0 ? 'text-success' : 'text-error'">{{ formatMoney(rangeSummary.estimatedMultiPnlRs) }}</strong>
              </span>
              <span class="ml-4">
                Per-share — Single
                <strong :class="rangeSummary.backtestSinglePerShare >= 0 ? 'text-success' : 'text-error'">{{ rangeSummary.backtestSinglePerShare.toFixed(3) }}</strong>
                · Multi
                <strong :class="rangeSummary.backtestMultiPerShare >= 0 ? 'text-success' : 'text-error'">{{ rangeSummary.backtestMultiPerShare.toFixed(3) }}</strong>
              </span>
            </div>

            <!-- Risk/exposure crossover A/B comparison (2026-08-02 review):
                 the row above ("Estimated ₹") is Option B -- today's real
                 sizing, unchanged. This row is Option A -- the SAME
                 simulated entries/stops/exits (neither option touches
                 stop_buffer_pct), resized as if the exposure ceiling were
                 raised enough for risk_per_trade_rs to fully bind. See
                 estimatedOptionA in day-review.controllers.js's runRange(). -->
            <v-alert
              v-if="rangeSummary && rangeSummary.estimatedOptionA" type="info" variant="tonal"
              density="comfortable" class="mt-2" rounded="lg"
            >
              <strong>A/B comparison</strong> — same {{ rangeSummary.tradeCount }} {{ rangeSummary.mode === 'replay' ? 'real' : 'simulated' }} trade{{ rangeSummary.tradeCount === 1 ? '' : 's' }}, two sizing options:
              <div class="mt-1">
                <strong>Option B (today, unchanged):</strong> Net ₹ — Single
                <strong :class="rangeSummary.estimatedSingleNetPnlRs >= 0 ? 'text-success' : 'text-error'">{{ formatMoney(rangeSummary.estimatedSingleNetPnlRs) }}</strong>
                · Multi
                <strong :class="rangeSummary.estimatedMultiNetPnlRs >= 0 ? 'text-success' : 'text-error'">{{ formatMoney(rangeSummary.estimatedMultiNetPnlRs) }}</strong>
              </div>
              <div class="mt-1">
                <strong>Option A (₹{{ rangeSummary.estimatedOptionA.riskPerTradeRs }} risk fully applied, needs exposure ceiling ≥ ₹{{ rangeSummary.estimatedOptionA.minExposureRsNeeded.toLocaleString('en-IN') }}):</strong>
                Net ₹ — Single
                <strong :class="rangeSummary.estimatedOptionA.singleNetPnlRs >= 0 ? 'text-success' : 'text-error'">{{ formatMoney(rangeSummary.estimatedOptionA.singleNetPnlRs) }}</strong>
                · Multi
                <strong :class="rangeSummary.estimatedOptionA.multiNetPnlRs >= 0 ? 'text-success' : 'text-error'">{{ formatMoney(rangeSummary.estimatedOptionA.multiNetPnlRs) }}</strong>
              </div>
              <div class="text-caption text-medium-emphasis mt-1">
                Both wins and losses scale together under Option A — a bigger total here doesn't mean a better win
                rate, just larger position sizes on the same outcomes.
              </div>
            </v-alert>

            <!-- Dashboard Statistics (2026-08-03) -- win rate/profit factor/
                 expectancy/drawdown/streaks for this SAME simulated range,
                 reusing the exact math the Analytics tab uses for real
                 trades (see TradeStatsTiles.vue / buildDashboardStats() in
                 day-review.controllers.js). Stacked (not side by side) since
                 each block already has 6-7 tiles of its own -- matches every
                 other figure already on this page in ALSO covering both
                 exit-strategy variants, just full-width instead of squeezed
                 into half-width columns. -->
            <template v-if="rangeSummary && rangeSummary.dashboardStats">
              <TradeStatsTiles :stats="rangeSummary.dashboardStats.single" title="Single-stage exit" class="mt-2" />
              <TradeStatsTiles :stats="rangeSummary.dashboardStats.multi" title="Multi-stage exit" class="mt-3" />

              <!-- Equity curve + per-stock ranking, single/multi side by side
                   -- unlike the tile grids above, one chart or one table per
                   variant is narrow enough to sit comfortably at half-width,
                   matching how every other single/multi pair on this page is
                   already shown side by side rather than stacked. -->
              <v-row class="mt-3">
                <v-col cols="12" md="6">
                  <EquityCurveChart :daily="rangeSummary.dashboardStats.single.daily" title="Cumulative P&amp;L — Single-stage" />
                </v-col>
                <v-col cols="12" md="6">
                  <EquityCurveChart :daily="rangeSummary.dashboardStats.multi.daily" title="Cumulative P&amp;L — Multi-stage" />
                </v-col>
              </v-row>
              <v-row class="mt-2">
                <v-col cols="12" md="6">
                  <StockRankingTable :by-stock="rangeSummary.dashboardStats.single.byStock" title="Stock ranking — Single-stage" />
                </v-col>
                <v-col cols="12" md="6">
                  <StockRankingTable :by-stock="rangeSummary.dashboardStats.multi.byStock" title="Stock ranking — Multi-stage" />
                </v-col>
              </v-row>
            </template>

            <!-- The total above is just the sum of these -- shown explicitly,
                 like a calculator displaying its operands, so it's never a
                 number you have to take on faith. One row per trade (not per
                 day) since investment/qty/stop/first-profit-book are
                 per-trade properties -- a day with two stocks needs two rows. -->
            <div v-if="rangeSummary && rangeSummary.trades.length" class="mt-2">
              <div class="d-flex justify-end mb-2">
                <v-btn variant="text" size="small" @click="downloadRangeCsv">
                  <font-awesome-icon icon="fa-solid fa-download" class="mr-1" /> Download CSV
                </v-btn>
              </div>
              <div class="table-scroll">
              <v-table density="compact" class="custom-data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Stock</th>
                    <th>Entry</th>
                    <th>Est. investment</th>
                    <th>Est. qty</th>
                    <th>Initial SL</th>
                    <th>1st profit book</th>
                    <th>2nd profit book</th>
                    <th>Est. P&amp;L — Single</th>
                    <th>Est. P&amp;L — Multi</th>
                    <th v-if="rangeSummary.mode === 'replay'">Real P&amp;L</th>
                    <th v-if="rangeSummary.mode === 'replay'">Reward : risk</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="trade in rangeSummary.trades" :key="`${trade.date}-${trade.code}`">
                    <td>{{ trade.date }}</td>
                    <td>{{ trade.code }}</td>
                    <td>{{ $helper.formatPrice(trade.entryPrice) }}</td>
                    <td>{{ formatMoney(trade.capitalAllocatedRs) }}</td>
                    <td>{{ trade.quantity }}</td>
                    <td>{{ $helper.formatPrice(trade.stopPrice) }}</td>
                    <td>{{ trade.stage1ExitPrice !== null ? $helper.formatPrice(trade.stage1ExitPrice) : '—' }}</td>
                    <td>{{ trade.stage2ExitPrice !== null ? $helper.formatPrice(trade.stage2ExitPrice) : '—' }}</td>
                    <td :class="trade.estimatedSinglePnlRs >= 0 ? 'text-success' : 'text-error'">{{ formatMoney(trade.estimatedSinglePnlRs) }}</td>
                    <td :class="trade.estimatedMultiPnlRs >= 0 ? 'text-success' : 'text-error'">{{ formatMoney(trade.estimatedMultiPnlRs) }}</td>
                    <td v-if="rangeSummary.mode === 'replay'" :class="trade.realPnlRs !== null && trade.realPnlRs >= 0 ? 'text-success' : trade.realPnlRs !== null ? 'text-error' : ''">
                      {{ trade.realPnlRs !== null ? formatMoney(trade.realPnlRs) : '—' }}
                      <span v-if="trade.realStillOpen" class="text-caption text-medium-emphasis">(still open)</span>
                    </td>
                    <!-- Informational only -- this real trade already fired;
                         the badge just flags whether it would have been
                         blocked under the current reward:risk setting. -->
                    <td v-if="rangeSummary.mode === 'replay'">
                      <span v-if="trade.rewardRiskRatio !== null">
                        {{ trade.rewardRiskRatio.toFixed(2) }} : 1
                        <v-chip v-if="trade.wouldBeGatedByRewardRisk" size="small" color="warning" variant="tonal" class="ml-1">would be gated</v-chip>
                      </span>
                      <span v-else class="text-medium-emphasis">—</span>
                    </td>
                  </tr>
                  <tr class="range-total-row">
                    <td colspan="8" class="text-medium-emphasis">Total</td>
                    <td :class="rangeSummary.estimatedSinglePnlRs >= 0 ? 'text-success' : 'text-error'"><strong>{{ formatMoney(rangeSummary.estimatedSinglePnlRs) }}</strong></td>
                    <td :class="rangeSummary.estimatedMultiPnlRs >= 0 ? 'text-success' : 'text-error'"><strong>{{ formatMoney(rangeSummary.estimatedMultiPnlRs) }}</strong></td>
                    <td v-if="rangeSummary.mode === 'replay'"></td>
                    <td v-if="rangeSummary.mode === 'replay'"></td>
                  </tr>
                </tbody>
              </v-table>
              </div>
            </div>
          </template>
        </v-card-text>
      </v-card>

      <v-row v-if="!['decision-log', 'analytics'].includes(mode)" class="mb-1" dense>
        <v-col cols="12" sm="3">
          <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
            <v-card-text class="px-5 py-4">
              <div class="text-caption text-medium-emphasis">This date's P&amp;L</div>
              <div v-if="isFiniteNumber(datePnlRs)" class="stat-value" :class="datePnlRs >= 0 ? 'text-success' : 'text-error'">
                {{ formatMoney(datePnlRs) }}
              </div>
              <div v-else-if="isFiniteNumber(dateEstimatedPnlRs)" class="stat-value" :class="dateEstimatedPnlRs >= 0 ? 'text-success' : 'text-error'">
                {{ formatMoney(dateEstimatedPnlRs) }}
              </div>
              <div v-else class="stat-value stat-value--empty">—</div>
              <div class="text-caption text-medium-emphasis mt-1">
                <template v-if="isFiniteNumber(datePnlRs)">Real order</template>
                <template v-else-if="isFiniteNumber(dateEstimatedPnlRs)">Estimated — backtest only, no real order placed</template>
                <template v-else>Backtest only — no real order placed</template>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="3">
          <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
            <v-card-text class="px-5 py-4">
              <div class="text-caption text-medium-emphasis">This week's P&amp;L</div>
              <div v-if="week && week.realOrderCount > 0" class="stat-value" :class="Number(week.realPnlRs) >= 0 ? 'text-success' : 'text-error'">
                {{ formatMoney(week.realPnlRs) }}
              </div>
              <div v-else-if="week" class="stat-value" :class="week.estimatedSinglePnlRs >= 0 ? 'text-success' : 'text-error'">
                {{ formatMoney(week.estimatedSinglePnlRs) }}
              </div>
              <div v-else class="stat-value stat-value--empty">—</div>
              <div v-if="week" class="text-caption text-medium-emphasis mt-1">
                {{ week.from }} – {{ week.to }} ({{ week.tradeCount }} trade{{ week.tradeCount === 1 ? '' : 's' }})
                <template v-if="week.realOrderCount > 0">· real order{{ week.realOrderCount === 1 ? '' : 's' }}</template>
                <template v-else>· estimated (single-stage), no real order this week</template>
              </div>
              <div v-if="week" class="text-caption text-medium-emphasis">
                {{ week.realOrderCount > 0 ? 'Backtest' : 'Estimated' }}/share — single
                <span :class="week.backtestSinglePerShare >= 0 ? 'text-success' : 'text-error'">{{ week.backtestSinglePerShare.toFixed(3) }}</span>,
                multi
                <span :class="week.backtestMultiPerShare >= 0 ? 'text-success' : 'text-error'">{{ week.backtestMultiPerShare.toFixed(3) }}</span>
                <template v-if="week.realOrderCount > 0">
                  · est. multi P&amp;L {{ formatMoney(week.estimatedMultiPnlRs) }}
                </template>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="3">
          <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
            <v-card-text class="px-5 py-4">
              <div class="text-caption text-medium-emphasis">All-time P&amp;L</div>
              <div v-if="allTimeRealOrderCount > 0" class="stat-value" :class="Number(allTimePnlRs) >= 0 ? 'text-success' : 'text-error'">
                {{ formatMoney(allTimePnlRs) }}
              </div>
              <div v-else class="stat-value" :class="Number(allTimeEstimatedPnlRs) >= 0 ? 'text-success' : 'text-error'">
                {{ formatMoney(allTimeEstimatedPnlRs) }}
              </div>
              <div class="text-caption text-medium-emphasis mt-1">
                {{ allTimeRealOrderCount > 0 ? `${allTimeRealOrderCount} real order${allTimeRealOrderCount === 1 ? '' : 's'}, every date` : 'Estimated (single-stage) — no real order yet, every date' }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="3">
          <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
            <v-card-text class="px-5 py-4">
              <div class="text-caption text-medium-emphasis">Ranked-out P&amp;L (simulated)</div>
              <div
                v-if="shadowStats && shadowStats.allTimeShadowCount > 0"
                class="stat-value text-medium-emphasis"
                :class="Number(shadowStats.allTimeShadowPnlRs) >= 0 ? 'text-success' : 'text-error'"
              >
                {{ formatMoney(shadowStats.allTimeShadowPnlRs) }}
              </div>
              <div v-else class="stat-value stat-value--empty">—</div>
              <div class="text-caption text-medium-emphasis mt-1">
                {{ shadowStats ? shadowStats.allTimeShadowCount : 0 }} ranked-out trade{{ shadowStats && shadowStats.allTimeShadowCount === 1 ? '' : 's' }}, every date · never counted in P&amp;L above
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-alert v-if="mode === 'live' && loaded && !visibleOrders.length" type="info" variant="tonal" density="comfortable" class="mb-4" rounded="lg">
        No live order has been placed today yet. This will fill in once the live cron opens a trade during market hours.
      </v-alert>
      <v-alert v-else-if="mode === 'history' && loaded && !visibleOrders.length" type="info" variant="tonal" density="comfortable" class="mb-4" rounded="lg">
        No order was placed on {{ date }}, and no backtest was run for this date either.
      </v-alert>

      <div v-for="order in visibleOrders" :key="order.code" class="mb-6">
        <v-card flat rounded="lg" class="gap-backtest-card mb-4">
          <v-card-title class="px-5 py-4 d-flex align-center justify-space-between flex-wrap ga-2">
            <span class="text-h6 font-weight-medium d-flex align-center ga-2">
              {{ order.code }} — {{ date }}
              <v-chip size="small" :color="order.source === 'live' ? 'success' : 'warning'" variant="tonal">
                {{ order.source === 'live' ? 'Live order' : 'Backtest only — no live order placed' }}
              </v-chip>
            </span>
            <v-btn v-if="mode === 'history'" color="primary" variant="tonal" rounded="lg" size="small" :loading="order.running" @click="runBacktest(order)">
              Run backtest for this day
            </v-btn>
          </v-card-title>
          <v-alert v-if="order.message" :type="order.messageError ? 'error' : 'success'" variant="tonal" density="comfortable" class="mx-5 mb-4" rounded="lg">
            {{ order.message }}
          </v-alert>
          <v-card-text class="px-5 pb-5">
            <DayReviewChart :candles="order.candles" :live-trade="order.liveTrade" :backtest-result="order.backtestResult" />
          </v-card-text>
        </v-card>

        <v-row>
          <v-col cols="12" md="6">
            <v-card flat rounded="lg" class="gap-backtest-card">
              <v-card-title class="px-5 py-4">
                <span class="text-h6 font-weight-medium">Live Trade</span>
              </v-card-title>
              <v-card-text class="px-5 pb-5">
                <v-table v-if="order.liveTrade" density="compact">
                  <tbody>
                    <tr>
                      <td class="text-medium-emphasis">Status</td>
                      <td><v-chip size="small" variant="tonal">{{ order.liveTrade.status }}</v-chip></td>
                    </tr>
                    <tr>
                      <td class="text-medium-emphasis">Entry time</td>
                      <td>{{ formatClockIST(order.liveTrade.opened_at) }}</td>
                    </tr>
                    <tr>
                      <td class="text-medium-emphasis">Entry price</td>
                      <td>{{ $helper.formatPrice(order.liveTrade.entry_price) }}</td>
                    </tr>
                    <tr>
                      <td class="text-medium-emphasis">Stop loss</td>
                      <td>
                        {{ order.liveTrade.stop_price ? $helper.formatPrice(order.liveTrade.stop_price) : '—' }}
                        <span
                          v-if="order.liveTrade.initial_stop_price !== null && order.liveTrade.initial_stop_price !== undefined && Number(order.liveTrade.initial_stop_price) !== Number(order.liveTrade.stop_price)"
                          class="text-caption text-medium-emphasis"
                        >
                          (moved from {{ $helper.formatPrice(order.liveTrade.initial_stop_price) }})
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="text-medium-emphasis">Target</td>
                      <td>{{ order.liveTrade.target_price ? $helper.formatPrice(order.liveTrade.target_price) : '—' }}</td>
                    </tr>
                    <tr>
                      <td class="text-medium-emphasis">Reward : risk</td>
                      <td>{{ formatRewardRisk(order.liveTrade) }}</td>
                    </tr>
                    <tr v-if="order.liveTrade.partial_booked">
                      <td class="text-medium-emphasis">Partial exit</td>
                      <td>
                        {{ $helper.formatPrice(order.liveTrade.partial_exit_price) }} &times; {{ order.liveTrade.partial_exit_qty }} shares
                        <span :class="Number(order.liveTrade.partial_pnl_rs) >= 0 ? 'text-success' : 'text-error'">
                          ({{ formatMoney(order.liveTrade.partial_pnl_rs) }})
                        </span>
                      </td>
                    </tr>
                    <tr v-if="order.liveTrade.stage1_booked">
                      <td class="text-medium-emphasis">Stage 1 exit</td>
                      <td>
                        {{ $helper.formatPrice(order.liveTrade.stage1_exit_price) }} &times; {{ order.liveTrade.stage1_exit_qty }} shares
                        <span :class="Number(order.liveTrade.stage1_pnl_rs) >= 0 ? 'text-success' : 'text-error'">
                          ({{ formatMoney(order.liveTrade.stage1_pnl_rs) }})
                        </span>
                      </td>
                    </tr>
                    <tr v-if="order.liveTrade.stage2_booked">
                      <td class="text-medium-emphasis">Stage 2 exit</td>
                      <td>
                        {{ $helper.formatPrice(order.liveTrade.stage2_exit_price) }} &times; {{ order.liveTrade.stage2_exit_qty }} shares
                        <span :class="Number(order.liveTrade.stage2_pnl_rs) >= 0 ? 'text-success' : 'text-error'">
                          ({{ formatMoney(order.liveTrade.stage2_pnl_rs) }})
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="text-medium-emphasis">Exit</td>
                      <td>
                        {{ order.liveTrade.exit_price ? `${$helper.formatPrice(order.liveTrade.exit_price)} (${order.liveTrade.exit_reason})` : '—' }}
                        <span v-if="order.liveTrade.closed_at">at {{ formatClockIST(order.liveTrade.closed_at) }}</span>
                      </td>
                    </tr>
                    <tr>
                      <td class="text-medium-emphasis">P&amp;L (net of charges)</td>
                      <td :class="Number(order.liveTrade.pnl_rs) >= 0 ? 'text-success' : 'text-error'">
                        {{ order.liveTrade.pnl_rs !== null ? $helper.formatPrice(order.liveTrade.pnl_rs) : '—' }}
                      </td>
                    </tr>
                    <tr v-if="order.liveTrade.total_charges_rs !== null && order.liveTrade.total_charges_rs !== undefined">
                      <td class="text-medium-emphasis">Charges (brokerage/STT/GST/etc.)</td>
                      <td>{{ formatMoney(order.liveTrade.total_charges_rs) }}</td>
                    </tr>
                    <tr>
                      <td class="text-medium-emphasis">Capital invested</td>
                      <td>{{ formatMoney(order.liveTrade.capital_allocated) }}</td>
                    </tr>
                    <tr>
                      <td class="text-medium-emphasis">Qty</td>
                      <td>
                        {{ order.liveTrade.quantity ?? '—' }}
                        <span v-if="order.liveTrade.remaining_quantity !== null && order.liveTrade.remaining_quantity !== order.liveTrade.quantity">
                          ({{ order.liveTrade.remaining_quantity }} remaining)
                        </span>
                      </td>
                    </tr>
                    <tr v-if="order.liveTrade.rank_position !== null">
                      <td class="text-medium-emphasis">Rank / confidence</td>
                      <td>
                        #{{ order.liveTrade.rank_position }} ·
                        conf. {{ formatPct(order.liveTrade.today_confidence_pct) }} ·
                        win rate {{ formatPct(order.liveTrade.historical_win_rate_pct) }}
                        (Wilson {{ formatPct(order.liveTrade.wilson_lower_bound_pct) }})
                      </td>
                    </tr>
                  </tbody>
                </v-table>
                <div v-else-if="order.estimate">
                  <span class="text-medium-emphasis">No live order was placed for this date/stock. Estimated as if it had been:</span>
                  <v-table density="compact" class="mt-1">
                    <tbody>
                      <tr>
                        <td class="text-medium-emphasis">Entry time</td>
                        <td>{{ order.candles.length ? formatClockCandle(order.candles[0].timestamp) : '—' }}</td>
                      </tr>
                      <tr>
                        <td class="text-medium-emphasis">Entry price</td>
                        <td>{{ order.backtestResult ? $helper.formatPrice(order.backtestResult.entry_price) : '—' }}</td>
                      </tr>
                      <tr>
                        <td class="text-medium-emphasis">Stop loss (est.)</td>
                        <td>{{ formatMoney(order.estimate.stopPrice) }}</td>
                      </tr>
                      <tr>
                        <td class="text-medium-emphasis">Est. qty / capital</td>
                        <td>{{ order.estimate.quantity }} · {{ formatMoney(order.estimate.capitalAllocatedRs) }}</td>
                      </tr>
                      <tr>
                        <td class="text-medium-emphasis">Est. P&amp;L</td>
                        <td>
                          Single: <span :class="order.estimate.singlePnlRs >= 0 ? 'text-success' : 'text-error'">{{ formatMoney(order.estimate.singlePnlRs) }}</span> ·
                          Multi: <span :class="order.estimate.multiPnlRs >= 0 ? 'text-success' : 'text-error'">{{ formatMoney(order.estimate.multiPnlRs) }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </v-table>
                  <div class="text-caption text-medium-emphasis mt-1">
                    (Sized using current settings against this trade alone — not a real allocation, just a "what would this have cost" estimate.)
                  </div>
                </div>
                <span v-else class="text-medium-emphasis">
                  No live order was placed for this date/stock — shown from the offline backtest instead.
                </span>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card flat rounded="lg" class="gap-backtest-card">
              <v-card-title class="px-5 py-4">
                <span class="text-h6 font-weight-medium">Backtest Comparison</span>
              </v-card-title>
              <v-card-text class="px-5 pb-5">
                <v-table v-if="order.backtestResult" density="compact">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Single-stage</th>
                      <th>Multi-stage</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="text-medium-emphasis">Entry time</td>
                      <td colspan="2">{{ order.candles.length ? formatClockCandle(order.candles[0].timestamp) : '—' }}</td>
                    </tr>
                    <tr>
                      <td class="text-medium-emphasis">Entry price</td>
                      <td colspan="2">{{ $helper.formatPrice(order.backtestResult.entry_price) }}</td>
                    </tr>
                    <tr>
                      <td class="text-medium-emphasis">Stop loss</td>
                      <td colspan="2">{{ formatMoney(order.backtestStopPrice) }}</td>
                    </tr>
                    <tr>
                      <td class="text-medium-emphasis">Exit</td>
                      <td>{{ order.backtestResult.single_exit_reason }} @ {{ $helper.formatPrice(order.backtestResult.single_exit_price) }}</td>
                      <td>
                        {{ order.backtestResult.multi_exit_reason }} @ {{ $helper.formatPrice(order.backtestResult.multi_exit_price) }}
                        <div class="text-caption text-medium-emphasis">stage1={{ order.backtestResult.multi_stage1_booked }} stage2={{ order.backtestResult.multi_stage2_booked }}</div>
                      </td>
                    </tr>
                    <tr>
                      <td class="text-medium-emphasis">P&amp;L / share</td>
                      <td :class="Number(order.backtestResult.single_pnl_per_share) >= 0 ? 'text-success' : 'text-error'">
                        {{ Number(order.backtestResult.single_pnl_per_share).toFixed(3) }}
                      </td>
                      <td :class="Number(order.backtestResult.multi_pnl_per_share) >= 0 ? 'text-success' : 'text-error'">
                        {{ Number(order.backtestResult.multi_pnl_per_share).toFixed(3) }}
                      </td>
                    </tr>
                    <tr v-if="order.estimate">
                      <td class="text-medium-emphasis">Est. P&amp;L (₹)</td>
                      <td :class="order.estimate.singlePnlRs >= 0 ? 'text-success' : 'text-error'">{{ formatMoney(order.estimate.singlePnlRs) }}</td>
                      <td :class="order.estimate.multiPnlRs >= 0 ? 'text-success' : 'text-error'">{{ formatMoney(order.estimate.multiPnlRs) }}</td>
                    </tr>
                  </tbody>
                </v-table>
                <span v-else class="text-medium-emphasis">
                  {{ mode === 'history' ? 'No backtest comparison for this date/stock yet — click "Run backtest for this day".' : 'No backtest comparison for today yet.' }}
                </span>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <v-dialog v-model="explorerDialog" max-width="960" scrollable>
        <v-card rounded="lg">
          <v-card-title class="d-flex align-center justify-space-between">
            <span>Trade Explorer — {{ explorerCode }}<template v-if="explorerTrade"> · {{ explorerTrade.trade_date }}</template></span>
            <v-btn icon variant="text" size="small" @click="explorerDialog = false">
              <font-awesome-icon icon="fa-solid fa-xmark" />
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-progress-linear v-if="explorerLoading" indeterminate class="mb-3" />
            <template v-else-if="explorerTrade">
              <DayReviewChart :candles="explorerCandles" :live-trade="explorerTrade" :backtest-result="null" />

              <v-row class="mt-3">
                <v-col cols="12" md="6">
                  <div class="text-subtitle-2 font-weight-medium mb-2">Trade detail</div>
                  <v-table density="compact">
                    <tbody>
                      <tr>
                        <td class="text-medium-emphasis">Status</td>
                        <td><v-chip size="small" variant="tonal" :color="statusColor(explorerTrade.status)">{{ explorerTrade.status }}</v-chip></td>
                      </tr>
                      <tr>
                        <td class="text-medium-emphasis">Gap %</td>
                        <td>{{ explorerTrade.gap_pct !== null && explorerTrade.gap_pct !== undefined ? `${Number(explorerTrade.gap_pct).toFixed(2)}%` : '—' }} ({{ explorerTrade.gap_type || '—' }})</td>
                      </tr>
                      <tr>
                        <td class="text-medium-emphasis">Action</td>
                        <td>{{ actionLabel(explorerTrade) }}</td>
                      </tr>
                      <tr>
                        <td class="text-medium-emphasis">Confidence / Win rate</td>
                        <td>{{ formatPct(explorerTrade.today_confidence_pct) }} · {{ formatPct(explorerTrade.historical_win_rate_pct) }} (Wilson {{ formatPct(explorerTrade.wilson_lower_bound_pct) }})</td>
                      </tr>
                      <tr>
                        <td class="text-medium-emphasis">Rank</td>
                        <td>{{ explorerTrade.rank_position !== null && explorerTrade.rank_position !== undefined ? `#${explorerTrade.rank_position}` : '—' }}</td>
                      </tr>
                      <tr>
                        <td class="text-medium-emphasis">Entry</td>
                        <td>{{ formatClockIST(explorerTrade.opened_at) }} @ {{ $helper.formatPrice(explorerTrade.entry_price) }}</td>
                      </tr>
                      <tr>
                        <td class="text-medium-emphasis">Stop / Target</td>
                        <td>{{ $helper.formatPrice(explorerTrade.stop_price) }} / {{ $helper.formatPrice(explorerTrade.target_price) }}</td>
                      </tr>
                      <tr v-if="explorerTrade.partial_booked">
                        <td class="text-medium-emphasis">Partial</td>
                        <td>{{ $helper.formatPrice(explorerTrade.partial_exit_price) }} &times; {{ explorerTrade.partial_exit_qty }} ({{ formatMoney(explorerTrade.partial_pnl_rs) }})</td>
                      </tr>
                      <tr v-if="explorerTrade.stage1_booked">
                        <td class="text-medium-emphasis">Stage 1</td>
                        <td>{{ $helper.formatPrice(explorerTrade.stage1_exit_price) }} &times; {{ explorerTrade.stage1_exit_qty }} ({{ formatMoney(explorerTrade.stage1_pnl_rs) }})</td>
                      </tr>
                      <tr v-if="explorerTrade.stage2_booked">
                        <td class="text-medium-emphasis">Stage 2</td>
                        <td>{{ $helper.formatPrice(explorerTrade.stage2_exit_price) }} &times; {{ explorerTrade.stage2_exit_qty }} ({{ formatMoney(explorerTrade.stage2_pnl_rs) }})</td>
                      </tr>
                      <tr>
                        <td class="text-medium-emphasis">Exit</td>
                        <td>
                          {{ explorerTrade.exit_price ? `${$helper.formatPrice(explorerTrade.exit_price)} (${explorerTrade.exit_reason})` : '—' }}
                          <span v-if="explorerTrade.closed_at">at {{ formatClockIST(explorerTrade.closed_at) }}</span>
                        </td>
                      </tr>
                      <tr>
                        <td class="text-medium-emphasis">Qty / Capital</td>
                        <td>{{ explorerTrade.quantity ?? '—' }} · {{ formatMoney(explorerTrade.capital_allocated) }}</td>
                      </tr>
                      <tr>
                        <td class="text-medium-emphasis">P&amp;L (net of charges)</td>
                        <td :class="Number(explorerTrade.pnl_rs) >= 0 ? 'text-success' : 'text-error'">
                          {{ explorerTrade.pnl_rs !== null && explorerTrade.pnl_rs !== undefined ? formatMoney(explorerTrade.pnl_rs) : '—' }}
                        </td>
                      </tr>
                    </tbody>
                  </v-table>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="text-subtitle-2 font-weight-medium mb-2">Timeline</div>
                  <v-alert v-if="!explorerEvents.length" type="info" variant="tonal" density="comfortable" rounded="lg">
                    No timeline events recorded for this trade.
                  </v-alert>
                  <div v-else class="timeline-list">
                    <div v-for="(event, i) in explorerEvents" :key="event.paper_trade_event_id" class="timeline-row">
                      <span class="timeline-index">{{ i + 1 }}</span>
                      <div class="timeline-body">
                        <div class="d-flex align-center ga-2">
                          <strong>{{ eventTypeLabel(event.event_type) }}</strong>
                          <span class="text-caption text-medium-emphasis">{{ formatClockIST(event.event_time) }}</span>
                        </div>
                        <div class="text-body-2 text-medium-emphasis">{{ eventSummaryText(event) }}</div>
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </template>
          </v-card-text>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import { fetchWrapper } from '@/helpers/fetch-wrapper';
import DayReviewChart from './DayReviewChart.vue';
import TradeStatsTiles from './TradeStatsTiles.vue';
import EquityCurveChart from './EquityCurveChart.vue';
import StockRankingTable from './StockRankingTable.vue';
import DateRangeFilter from '@/components/Report/filter/DateRangeFilter.vue';
import { Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

function todayDateKey() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Human-readable labels for paper_trade.decision_code -- see ORDER_SYSTEM.md's
// "Decision Log" section for the full table this mirrors. 'entered' isn't
// listed here since decisionReasonLabel() below shows the order_mode instead
// (more useful than a static "cleared every gate" string).
const DECISION_CODE_LABELS = {
  insufficient_history: 'Not enough daily history to classify yet',
  implausible_gap: 'Gap looked like bad data (exceeded sanity threshold)',
  invalid_quote: 'No valid quote yet',
  non_fade_gap: "Gap didn't classify as fade-bias (continuation/breakaway)",
  insufficient_backtest_events: 'Not enough historical gap events to judge',
  below_win_rate_threshold: "Historical win rate didn't clear the threshold",
  below_reward_risk_ratio: "Today's reward:risk ratio didn't clear the threshold",
  ranked_out: 'Ranked below the daily trade-slot limit',
  sector_limit_reached: 'Sector concentration limit reached',
  concurrent_limit_reached: 'Max concurrent open positions reached',
  zero_qty_after_sizing: 'Position size rounded to 0 shares within budget',
  live_order_failed: 'Live broker order failed to fill',
  daily_loss_limit: 'Daily loss limit already reached',
};

// Labels for paper_trade_event.event_type -- see
// bankend/app/helpers/paper-trade-events.js's TRADE_EVENT_TYPES (source of
// truth for the string values themselves).
const TRADE_EVENT_LABELS = {
  selected: 'Selected',
  order_placed: 'Order Placed',
  partial_booked: 'Partial Profit Booked',
  stop_moved_breakeven: 'Stop Moved to Breakeven',
  stage1_booked: 'Stage 1 Booked',
  stage2_booked: 'Stage 2 Booked',
  target_lock_applied: 'Target Lock Applied',
  closed: 'Closed',
  order_failed: 'Order Failed',
  slippage_alert: 'Slippage Alert',
};

export default {
  name: 'DayReview-page',
  components: { DayReviewChart, DateRangeFilter, TradeStatsTiles, EquityCurveChart, StockRankingTable, Bar },
  data() {
    const queryDate = (this.$route?.query?.date && /^\d{4}-\d{2}-\d{2}$/.test(this.$route.query.date))
      ? this.$route.query.date
      : null;
    const initialDate = queryDate || todayDateKey();

    return {
      // A specific historical date came in via the URL (e.g. from the
      // Settings page's "View on Day Review" link) -- open straight into
      // History mode on that date rather than Live/today.
      mode: (queryDate && queryDate !== todayDateKey()) ? 'history' : 'live',
      date: initialDate,
      orders: [],
      loaded: false,
      datePnlRs: null,
      dateEstimatedPnlRs: null,
      allTimePnlRs: 0,
      allTimeRealOrderCount: 0,
      allTimeEstimatedPnlRs: 0,
      shadowStats: null,
      week: null,
      refreshTimer: null,
      rangeFrom: initialDate,
      rangeTo: initialDate,
      rangeMode: 'derived',
      runningRange: false,
      rangeMessage: '',
      rangeError: false,
      rangeSummary: null,
      decisionLogRange: { from: initialDate, to: initialDate },
      decisionLogCode: '',
      decisionLogRows: [],
      decisionLogLoaded: true,
      divergenceRange: { from: initialDate, to: initialDate },
      divergenceSummary: null,
      divergenceWeekly: [],
      divergenceLoaded: true,
      priceIntegrityRange: { from: initialDate, to: initialDate },
      priceIntegrityCode: '',
      priceIntegrityData: null,
      priceIntegrityLoaded: true,
      // Empty from/to on first load lets the backend apply its own default
      // (rolling 90 days) -- loadAnalytics() then fills these in from the
      // response so the date-range picker reflects what was actually loaded.
      analyticsRange: { from: '', to: '' },
      analyticsData: null,
      analyticsLoaded: true,
      // Matches the backend's actual response keys directly (daily/weekly/
      // monthly, see GET /api/day-review/analytics) -- bucketChartData()
      // below indexes analyticsData with this value, so it must equal one
      // of those keys exactly, not a shorthand ('day') that was never a
      // real key and silently produced an always-empty chart.
      analyticsBucket: 'daily',
      bucketChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => ` ₹${Number(ctx.parsed.y).toLocaleString('en-IN')}` } },
        },
        scales: {
          x: { grid: { display: false }, ticks: { autoSkip: true, maxTicksLimit: 12 } },
          y: { grid: { color: '#e1e0d9' } },
        },
      },
      explorerDialog: false,
      explorerLoading: false,
      explorerCode: '',
      explorerTrade: null,
      explorerCandles: [],
      explorerEvents: [],
    };
  },
  computed: {
    // Live mode only ever shows real orders -- a backtest-fallback row (no
    // live order yet today) is meaningful in History mode but out of place
    // under a "Live" tab, so it's filtered out rather than shown as-is.
    visibleOrders() {
      if (['decision-log', 'divergence', 'analytics', 'price-integrity'].includes(this.mode)) return [];
      return this.mode === 'live' ? this.orders.filter((o) => o.source === 'live') : this.orders;
    },
    // byStock is a plain object keyed by code (JSON-friendly API shape) --
    // sorted by flagged count desc so a stock with real findings surfaces
    // above ones that only had missing-data skips.
    priceIntegrityByStockRows() {
      const byStock = this.priceIntegrityData?.byStock || {};
      return Object.entries(byStock)
        .map(([code, stats]) => ({ code, ...stats }))
        .sort((a, b) => b.flagged - a.flagged || a.code.localeCompare(b.code));
    },
    // True once the loaded range straddles the transaction-cost formula
    // change (see ORDER_SYSTEM.md's "Transaction-cost accounting") -- the
    // backend reports the cutover date so this component never hardcodes it.
    showsFormulaBreakCaveat() {
      const d = this.analyticsData;
      return Boolean(d && d.formulaBreakDate && d.from <= d.formulaBreakDate && d.to >= d.formulaBreakDate);
    },
    bucketChartData() {
      const rows = this.analyticsData?.[this.analyticsBucket] || [];
      return {
        labels: rows.map((r) => r.period),
        datasets: [{
          label: 'P&L (₹)',
          data: rows.map((r) => Number(r.pnlRs) || 0),
          backgroundColor: rows.map((r) => (Number(r.pnlRs) >= 0 ? '#0ca30c' : '#d03b3b')),
          borderRadius: 4,
          barPercentage: 0.7,
          categoryPercentage: 0.8,
        }],
      };
    },
  },
  watch: {
    date() { this.load(); },
  },
  mounted() {
    this.load();
    if (this.mode === 'live') this.startAutoRefresh();
  },
  beforeUnmount() {
    this.stopAutoRefresh();
  },
  methods: {
    load() {
      if (!this.date) return Promise.resolve();
      this.loaded = false;

      return fetchWrapper.get(`api/day-review?date=${this.date}`).then((res) => {
        this.orders = (res?.data?.orders || []).map((order) => ({
          ...order,
          running: false,
          message: '',
          messageError: false,
        }));
        this.datePnlRs = res?.data?.datePnlRs ?? null;
        this.dateEstimatedPnlRs = res?.data?.dateEstimatedPnlRs ?? null;
        this.allTimePnlRs = res?.data?.allTimePnlRs ?? 0;
        this.allTimeRealOrderCount = res?.data?.allTimeRealOrderCount ?? 0;
        this.allTimeEstimatedPnlRs = res?.data?.allTimeEstimatedPnlRs ?? 0;
        this.shadowStats = res?.data?.shadowStats ?? null;
        this.week = res?.data?.week ?? null;
        this.loaded = true;
      }).catch((err) => {
        console.error('Failed to load day review:', err);
        this.orders = [];
        this.datePnlRs = null;
        this.dateEstimatedPnlRs = null;
        this.allTimeRealOrderCount = 0;
        this.allTimeEstimatedPnlRs = 0;
        this.week = null;
        this.loaded = true;
      });
    },
    switchToLive() {
      this.mode = 'live';
      this.date = todayDateKey();
      this.load();
      this.startAutoRefresh();
    },
    switchToHistory() {
      this.mode = 'history';
      this.stopAutoRefresh();
    },
    switchToDecisionLog() {
      this.mode = 'decision-log';
      this.stopAutoRefresh();
      if (!this.decisionLogRows.length) this.loadDecisionLog();
    },
    switchToDivergence() {
      this.mode = 'divergence';
      this.stopAutoRefresh();
      if (!this.divergenceWeekly.length) this.loadDivergence();
    },
    switchToAnalytics() {
      this.mode = 'analytics';
      this.stopAutoRefresh();
      if (!this.analyticsData) this.loadAnalytics();
    },
    switchToPriceIntegrity() {
      this.mode = 'price-integrity';
      this.stopAutoRefresh();
      if (!this.priceIntegrityData) this.loadPriceIntegrity();
    },
    startAutoRefresh() {
      this.stopAutoRefresh();
      this.refreshTimer = setInterval(() => {
        if (this.mode === 'live') this.load();
      }, 20000);
    },
    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },
    isFiniteNumber(value) {
      return value !== null && value !== undefined && Number.isFinite(Number(value));
    },
    // Number(null) is 0 (finite), not NaN -- checking isFiniteNumber() first
    // is required so a genuinely absent value (e.g. a week with no decided
    // trades) renders as "no data", not a misleading "0.0%".
    formatPct(value) {
      return this.isFiniteNumber(value) ? `${Number(value).toFixed(1)}%` : '—';
    },
    formatSignedPct(value) {
      if (!this.isFiniteNumber(value)) return '—';
      const n = Number(value);
      return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
    },
    deltaClass(value) {
      return this.isFiniteNumber(value) ? (Number(value) >= 0 ? 'text-success' : 'text-error') : '';
    },
    formatMoney(value) {
      return this.isFiniteNumber(value) ? this.$helper.formatPrice(Number(value)) : '—';
    },
    // initial_stop_price (the original risk) rather than stop_price (which
    // can move to breakeven/trail) -- otherwise a trade that already booked
    // a partial would show an inflated ratio against its now-moved stop.
    formatRewardRisk(trade) {
      if (!trade) return '—';
      const stopBasis = this.isFiniteNumber(trade.initial_stop_price) ? trade.initial_stop_price : trade.stop_price;
      const ratio = this.$helper.rewardRiskRatio(trade.entry_price, trade.target_price, stopBasis);
      return ratio !== null ? `${ratio.toFixed(2)} : 1` : '—';
    },
    // opened_at/closed_at are real UTC instants (normal Sequelize DATETIME) --
    // convert properly to IST wall-clock for display.
    formatClockIST(value) {
      if (!value) return '—';
      return new Date(value).toLocaleTimeString('en-IN', {
        timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    },
    // candle_time is a genuine UTC instant (Angel One returns IST-with-offset
    // timestamps, correctly parsed) -- DayReviewChart.vue's own +5:30 shift
    // before feeding lightweight-charts (which always renders its axis as if
    // in UTC) proves this: it needs the shift to *display* as IST, meaning
    // the stored value itself is real UTC. So the same IST conversion used
    // for opened_at/closed_at applies here too -- no special-casing needed.
    formatClockCandle(value) {
      return this.formatClockIST(value);
    },
    runBacktest(order) {
      order.running = true;
      order.message = '';
      const { code } = order;
      const date = this.date;

      fetchWrapper.post('api/day-review/run', { date, code }).then(() => {
        // Reload the whole page's data rather than just patching
        // order.backtestResult -- backtestStopPrice/estimate (and the stat
        // tiles) are only ever computed by the GET handler, so a targeted
        // patch left them stale until a full page reload. `this.orders` is
        // a new array after load(), so the success message is set on the
        // freshly-loaded order object, not the stale one this closure
        // captured.
        return this.load().then(() => {
          const refreshed = this.orders.find((o) => o.code === code);
          if (refreshed) {
            refreshed.message = `Backtest simulated for ${code} on ${date}.`;
            refreshed.messageError = false;
          }
        });
      }).catch((err) => {
        const target = this.orders.find((o) => o.code === code) || order;
        target.message = typeof err === 'string' ? err : `Failed to run backtest for ${code} on ${date}.`;
        target.messageError = true;
      }).finally(() => {
        const target = this.orders.find((o) => o.code === code);
        (target || order).running = false;
      });
    },
    runRange() {
      if (!this.rangeFrom) return;
      this.runningRange = true;
      this.rangeMessage = '';
      const toDate = this.rangeTo || this.rangeFrom;
      const isReplay = this.rangeMode === 'replay';
      const url = isReplay ? 'api/day-review/run-range-replay' : 'api/day-review/run-range';
      fetchWrapper.post(url, { fromDate: this.rangeFrom, toDate }).then((res) => {
        const count = res?.data?.tradeCount ?? 0;
        this.rangeMessage = isReplay
          ? `${count} real trade(s) replayed across ${this.rangeFrom} to ${toDate} under current settings.`
          : `${count} trade(s) simulated across ${this.rangeFrom} to ${toDate} — showing ${this.rangeFrom} below (change the Date field above for any other day in the range).`;
        this.rangeError = false;
        this.rangeSummary = res?.data ? {
          fromDate: res.data.fromDate,
          toDate: res.data.toDate,
          tradeCount: res.data.tradeCount,
          backtestSinglePerShare: res.data.backtestSinglePerShare,
          backtestMultiPerShare: res.data.backtestMultiPerShare,
          estimatedSinglePnlRs: res.data.estimatedSinglePnlRs,
          estimatedMultiPnlRs: res.data.estimatedMultiPnlRs,
          estimatedSingleNetPnlRs: res.data.estimatedSingleNetPnlRs,
          estimatedMultiNetPnlRs: res.data.estimatedMultiNetPnlRs,
          estimatedOptionA: res.data.estimatedOptionA || null,
          dashboardStats: res.data.dashboardStats || null,
          trades: res.data.trades || [],
          // Only present on a derived-selection run (see runRange() in
          // day-review.controllers.js) -- candidates that never got
          // simulated at all because they didn't clear the reward:risk
          // gate, so they're never in `trades` either.
          gatedByRewardRisk: res.data.gatedByRewardRisk || [],
          mode: isReplay ? 'replay' : 'derived',
        } : null;
        // Only a derived-mode run jumps the single-day view -- it's the one
        // that persists into intraday_backtest_result, so the single-day
        // chart below has something new to show. A replay run computes
        // nothing that page reads (see runRangeReplay()'s no-persistence
        // design), so jumping there would just re-show the old, unrelated
        // chart and could be mistaken for a replay-specific view.
        if (!isReplay) {
          if (this.date === this.rangeFrom) {
            this.load();
          } else {
            this.date = this.rangeFrom;
          }
        }
      }).catch((err) => {
        this.rangeMessage = typeof err === 'string' ? err : `Failed to ${isReplay ? 'replay real trades for' : 'run backtest for'} ${this.rangeFrom} to ${toDate}.`;
        this.rangeError = true;
      }).finally(() => {
        this.runningRange = false;
      });
    },
    downloadRangeCsv() {
      if (!this.rangeSummary?.trades?.length) return;

      const isReplay = this.rangeSummary.mode === 'replay';
      const headers = [
        'Date', 'Stock', 'Entry', 'Est. investment (Rs)', 'Est. qty', 'Initial SL',
        '1st profit book', '2nd profit book', 'Single exit reason', 'Single P&L/share',
        'Multi exit reason', 'Multi P&L/share', 'Est. P&L Single (Rs)', 'Est. P&L Multi (Rs)',
        ...(isReplay ? ['Real P&L (Rs)', 'Real status', 'Reward:risk', 'Would be gated'] : []),
      ];
      const escapeCsv = (value) => {
        const s = value === null || value === undefined ? '' : String(value);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const rows = this.rangeSummary.trades.map((t) => [
        t.date, t.code, t.entryPrice, t.capitalAllocatedRs.toFixed(2), t.quantity, t.stopPrice.toFixed(2),
        t.stage1ExitPrice !== null ? t.stage1ExitPrice.toFixed(2) : '',
        t.stage2ExitPrice !== null ? t.stage2ExitPrice.toFixed(2) : '',
        t.singleExitReason, t.singlePnlPerShare.toFixed(3),
        t.multiExitReason, t.multiPnlPerShare.toFixed(3),
        t.estimatedSinglePnlRs.toFixed(2), t.estimatedMultiPnlRs.toFixed(2),
        ...(isReplay ? [
          t.realPnlRs !== null ? t.realPnlRs.toFixed(2) : '', t.realStillOpen ? 'open' : (t.realStatus || ''),
          t.rewardRiskRatio !== null ? t.rewardRiskRatio.toFixed(2) : '', t.wouldBeGatedByRewardRisk ? 'yes' : 'no',
        ] : []),
      ]);
      rows.push([
        'Total', '', '', '', '', '', '', '', '', '', '', '',
        this.rangeSummary.estimatedSinglePnlRs.toFixed(2), this.rangeSummary.estimatedMultiPnlRs.toFixed(2),
        ...(isReplay ? ['', '', '', ''] : []),
      ]);

      const csv = [headers, ...rows].map((row) => row.map(escapeCsv).join(',')).join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `day-review_${this.rangeSummary.fromDate}_to_${this.rangeSummary.toDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    // DateRangeFilter/v-date-picker can hand back either a plain string or a
    // Date object depending on how the user picked it -- the backend's
    // DATE_PATTERN needs a strict YYYY-MM-DD either way.
    toISODate(value) {
      if (!value) return '';
      if (typeof value === 'string') return value.slice(0, 10);
      const d = new Date(value);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },
    loadDecisionLog() {
      const from = this.toISODate(this.decisionLogRange?.from);
      if (!from) return;
      const to = this.toISODate(this.decisionLogRange?.to) || from;
      this.decisionLogLoaded = false;

      const params = new URLSearchParams({ from, to });
      if (this.decisionLogCode) params.set('code', this.decisionLogCode.trim().toUpperCase());

      return fetchWrapper.get(`api/day-review/decision-log?${params.toString()}`).then((res) => {
        this.decisionLogRows = res?.data?.rows || [];
        this.decisionLogLoaded = true;
      }).catch((err) => {
        console.error('Failed to load decision log:', err);
        this.decisionLogRows = [];
        this.decisionLogLoaded = true;
      });
    },
    loadDivergence() {
      const from = this.toISODate(this.divergenceRange?.from);
      if (!from) return;
      const to = this.toISODate(this.divergenceRange?.to) || from;
      this.divergenceLoaded = false;

      const params = new URLSearchParams({ from, to });

      return fetchWrapper.get(`api/day-review/divergence?${params.toString()}`).then((res) => {
        this.divergenceSummary = res?.data?.summary || null;
        this.divergenceWeekly = res?.data?.weekly || [];
        this.divergenceLoaded = true;
      }).catch((err) => {
        console.error('Failed to load divergence report:', err);
        this.divergenceSummary = null;
        this.divergenceWeekly = [];
        this.divergenceLoaded = true;
      });
    },
    loadPriceIntegrity() {
      const from = this.toISODate(this.priceIntegrityRange?.from);
      if (!from) return;
      const to = this.toISODate(this.priceIntegrityRange?.to) || from;
      this.priceIntegrityLoaded = false;

      const params = new URLSearchParams({ from, to });
      if (this.priceIntegrityCode) params.set('code', this.priceIntegrityCode.trim().toUpperCase());

      return fetchWrapper.get(`api/day-review/price-integrity?${params.toString()}`).then((res) => {
        this.priceIntegrityData = res?.data || null;
        this.priceIntegrityLoaded = true;
      }).catch((err) => {
        console.error('Failed to load price integrity check:', err);
        this.priceIntegrityData = null;
        this.priceIntegrityLoaded = true;
      });
    },
    priceIntegritySeverityColor(severity) {
      return severity === 'CONFIRMED_BAD_PRINT' ? 'error' : 'warning';
    },
    // Its own color scale, deliberately never reusing the severity chip's
    // red/amber -- severity and confidence are two separate dimensions and
    // should read as such at a glance, not as one combined signal.
    priceIntegrityConfidenceColor(confidence) {
      if (confidence === 'VERY_HIGH') return 'deep-purple';
      if (confidence === 'HIGH') return 'success';
      return 'amber-darken-2'; // MEDIUM
    },
    priceIntegrityConfidenceLabel(confidence) {
      if (confidence === 'VERY_HIGH') return 'Very high confidence';
      if (confidence === 'HIGH') return 'High confidence';
      return 'Medium confidence';
    },
    priceIntegritySkipReasonText(s) {
      if (s.reason === 'SPARSE_OPEN_COVERAGE' && s.marketOpenGapMinutes != null) {
        return `candle coverage starts ${s.marketOpenGapMinutes} min after market open, no daily quote history to corroborate`;
      }
      return s.reason;
    },
    // Mirrors decisionDetailText's style -- a compact, reason-specific string
    // rather than one column per flag/distance field. Shows the raw rupee
    // gap alongside the percentage, and says plainly when a second AngelOne
    // source (stock_history_<code>) corroborates the range -- "corroborated"
    // deliberately, not "cross-validated"/"independent": both
    // intraday_candle and stock_history_<code> trace back to AngelOne via
    // different API endpoints, not different exchanges, so agreement is
    // strong evidence, not proof.
    priceIntegrityDetailText(row) {
      const parts = [];
      if (row.flags.includes('ENTRY_MINUTE_MISMATCH')) {
        parts.push(`Entry not achievable in its own entry-minute candle (${this.$helper.formatPrice(row.entryCandle?.low)}–${this.$helper.formatPrice(row.entryCandle?.high)}${row.entryCandle?.volume != null ? `, volume ${row.entryCandle.volume}` : ''})`);
      }
      if (row.flags.includes('ENTRY_OPEN_MISMATCH')) {
        let openPart = `Entry vs. session open ${this.formatSignedPct(row.openMismatchPct)} (no entry-minute candle available, fallback check`;
        openPart += row.openAgreesWithStockHistory === true ? ', daily quote history agrees' : row.openAgreesWithStockHistory === false ? ', daily quote history disagrees' : '';
        openPart += ')';
        parts.push(openPart);
      }
      if (row.flags.includes('ENTRY_RANGE_BREACH')) {
        const overHigh = Number(row.entry_price) > row.realDayHigh;
        const boundaryLabel = overHigh ? 'verified day high' : 'verified day low';
        const boundaryValue = overHigh ? row.realDayHigh : row.realDayLow;
        const diffRs = (Number(row.entry_price) - boundaryValue).toFixed(2);
        let rangePart = `${this.$helper.formatPrice(row.entry_price)} vs. ${boundaryLabel} ${this.$helper.formatPrice(boundaryValue)} `
          + `(${overHigh ? '+' : ''}₹${diffRs} / ${row.entryDistancePct.toFixed(2)}%)`;
        if (row.dayRangeSource === 'combined') {
          rangePart += ' — corroborated by intraday candles + daily quote history (two AngelOne sources agree)';
        }
        parts.push(rangePart);
      }
      if (row.flags.includes('STOP_RANGE_BREACH')) {
        parts.push(`Stop ${row.stopDistancePct.toFixed(2)}% outside range (informational)`);
      }
      if (row.flags.includes('TARGET_RANGE_BREACH')) {
        parts.push(`Target ${row.targetDistancePct.toFixed(2)}% outside range (informational)`);
      }
      return parts.join(' · ');
    },
    downloadPriceIntegrityCsv() {
      const rows = this.priceIntegrityData?.flagged || [];
      if (!rows.length) return;

      const headers = [
        'Date', 'Stock', 'Severity', 'Confidence', 'Day range source', 'Entry', 'Verified day low', 'Verified day high',
        'Entry distance %', 'Stop distance %', 'Target distance %', 'Detail', 'Status', 'P&L (Rs)',
      ];
      const escapeCsv = (value) => {
        const s = value === null || value === undefined ? '' : String(value);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csvRows = rows.map((row) => [
        row.trade_date, row.code, row.severity, row.confidence, row.dayRangeSource, row.entry_price, row.realDayLow, row.realDayHigh,
        row.entryDistancePct, row.stopDistancePct, row.targetDistancePct,
        this.priceIntegrityDetailText(row), row.status, row.pnl_rs ?? '',
      ]);

      const csv = [headers, ...csvRows].map((row) => row.map(escapeCsv).join(',')).join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const from = this.toISODate(this.priceIntegrityRange?.from);
      const to = this.toISODate(this.priceIntegrityRange?.to) || from;
      link.download = `price-integrity_${from}_to_${to}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    loadAnalytics() {
      this.analyticsLoaded = false;
      const from = this.toISODate(this.analyticsRange?.from);
      const to = this.toISODate(this.analyticsRange?.to) || from;
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);

      return fetchWrapper.get(`api/day-review/analytics${params.toString() ? `?${params.toString()}` : ''}`).then((res) => {
        this.analyticsData = res?.data || null;
        // Reflects the backend's actual (possibly defaulted) range back into
        // the picker, so a bare "Load All" on first open shows what range it
        // actually got rather than leaving the fields blank.
        if (this.analyticsData) {
          this.analyticsRange = { from: this.analyticsData.from, to: this.analyticsData.to };
        }
        this.analyticsLoaded = true;
      }).catch((err) => {
        console.error('Failed to load analytics:', err);
        this.analyticsData = null;
        this.analyticsLoaded = true;
      });
    },
    // Sample-size confidence, NOT a P&L judgment -- do not reuse win/loss colors here.
    // This tier is independent of pnlRs/winRatePct; a row can be 'experimental' (grey)
    // and strongly profitable, or 'reliable' (green) and a loser. See trade-analytics.js
    // for the tier thresholds -- this function only controls chip color, not the tier itself.
    confidenceColor(tier) {
      if (tier === 'reliable') return 'success';
      if (tier === 'limited') return 'grey';
      return 'grey-darken-1';
    },
    confidenceLabel(tier) {
      if (tier === 'reliable') return 'Reliable';
      if (tier === 'limited') return 'Limited data';
      return 'Experimental';
    },
    statusColor(status) {
      if (status === 'open') return 'info';
      if (status === 'won') return 'success';
      if (status === 'lost') return 'error';
      if (status === 'closed_flat') return 'default';
      return 'warning'; // no_signal / skipped_*
    },
    // 'entered' shows the order_mode (more useful than a static label);
    // everything else looks up the reason label map above.
    decisionReasonLabel(row) {
      if (row.decision_code === 'entered') return `Entered (${row.order_mode || 'paper'})`;
      return DECISION_CODE_LABELS[row.decision_code] || (row.decision_code ? row.decision_code : '—');
    },
    // Direction is always faded (opposite the gap) -- see ORDER_SYSTEM.md:
    // a gap up is faded short, a gap down is faded long. Only meaningful once
    // a gap actually got classified (direction is null for insufficient
    // history/invalid quote rows).
    actionLabel(row) {
      if (row.direction === 'up') return 'SELL (fade the gap up)';
      if (row.direction === 'down') return 'BUY (fade the gap down)';
      return '—';
    },
    decisionDetailText(row) {
      if (row.decision_code === 'below_win_rate_threshold' && row.decision_detail) {
        return `needed ${this.formatPct(row.decision_detail.min_win_rate_threshold_pct)} win rate, ${row.decision_detail.gap_event_count} historical gaps`;
      }
      if (row.decision_code === 'below_reward_risk_ratio' && row.decision_detail) {
        const ratio = row.decision_detail.reward_risk_ratio;
        return `R:R ${ratio !== null ? ratio.toFixed(2) : '—'}:1, needs ≥ ${row.decision_detail.min_reward_risk_ratio}:1`;
      }
      if (row.decision_code === 'ranked_out' && row.decision_detail) {
        return `rank #${row.decision_detail.rank} of ${row.decision_detail.max_trades_per_day} slot(s)`;
      }
      if (row.decision_code === 'concurrent_limit_reached' && row.decision_detail) {
        return `${row.decision_detail.current_open_count} of ${row.decision_detail.max_concurrent_positions} concurrent position(s) already open`;
      }
      if (row.decision_code === 'sector_limit_reached' && row.decision_detail) {
        const d = row.decision_detail;
        return d.max_positions_per_sector !== undefined
          ? `${d.sector}: ${d.current_sector_positions} of ${d.max_positions_per_sector} position(s) already open`
          : `${d.sector}: ${this.formatMoney(d.current_sector_exposure_rs)} + ${this.formatMoney(d.candidate_exposure_rs)} would exceed ${this.formatMoney(d.max_sector_exposure_rs)} (${d.max_sector_exposure_pct}% cap)`;
      }
      if (row.decision_code === 'zero_qty_after_sizing' && row.decision_detail) {
        return `budget ${this.formatMoney(row.decision_detail.exposure_budget_rs)} at entry ${this.$helper.formatPrice(row.decision_detail.entry_price)}`;
      }
      if (row.decision_code === 'daily_loss_limit' && row.decision_detail) {
        return `realized ${this.formatMoney(row.decision_detail.realized_pnl_rs)} vs. limit ${this.formatMoney(-row.decision_detail.max_daily_loss_rs)}`;
      }
      return '—';
    },
    downloadDecisionLogCsv() {
      if (!this.decisionLogRows.length) return;

      const headers = [
        'Date', 'Stock', 'Status', 'Reason', 'Action', 'Gap %', 'Confidence',
        'Historical win rate', 'Wilson lower bound', 'Rank', 'Exit reason', 'P&L (Rs)', 'Detail',
      ];
      const escapeCsv = (value) => {
        const s = value === null || value === undefined ? '' : String(value);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const rows = this.decisionLogRows.map((row) => [
        row.trade_date, row.code, row.status, this.decisionReasonLabel(row), this.actionLabel(row),
        row.gap_pct ?? '', row.today_confidence_pct ?? '', row.historical_win_rate_pct ?? '',
        row.wilson_lower_bound_pct ?? '', row.rank_position ?? '', row.exit_reason ?? '', row.pnl_rs ?? '',
        this.decisionDetailText(row),
      ]);

      const csv = [headers, ...rows].map((row) => row.map(escapeCsv).join(',')).join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const from = this.toISODate(this.decisionLogRange?.from);
      const to = this.toISODate(this.decisionLogRange?.to) || from;
      link.download = `decision-log_${from}_to_${to}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    openTradeExplorer(row) {
      this.explorerCode = row.code;
      this.explorerDialog = true;
      this.explorerLoading = true;
      this.explorerTrade = null;
      this.explorerCandles = [];
      this.explorerEvents = [];

      fetchWrapper.get(`api/day-review/trade-detail?paperTradeId=${row.paper_trade_id}`).then((res) => {
        this.explorerTrade = res?.data?.trade || null;
        this.explorerCandles = res?.data?.candles || [];
        this.explorerEvents = res?.data?.events || [];
        this.explorerLoading = false;
      }).catch((err) => {
        console.error('Failed to load trade detail:', err);
        this.explorerTrade = null;
        this.explorerLoading = false;
      });
    },
    eventTypeLabel(type) {
      return TRADE_EVENT_LABELS[type] || type;
    },
    // A short one-line summary built from whatever this event actually
    // carries -- price/qty are real columns on every event, details is the
    // reason-specific JSON (see paper-trade-event.model.js).
    eventSummaryText(event) {
      const parts = [];
      if (event.price !== null && event.price !== undefined) parts.push(this.$helper.formatPrice(event.price));
      if (event.qty !== null && event.qty !== undefined && event.qty > 0) parts.push(`qty ${event.qty}`);
      const details = event.details || {};
      if (details.pnl_rs !== undefined) parts.push(`P&L ${this.formatMoney(details.pnl_rs)}`);
      if (details.from !== undefined && details.from !== null) parts.push(`from ${this.$helper.formatPrice(details.from)}`);
      if (details.confidence !== undefined) parts.push(`confidence ${this.formatPct(details.confidence)}`);
      if (details.rank !== undefined) parts.push(`rank #${details.rank}`);
      if (details.side) parts.push(details.side);
      if (details.mode) parts.push(details.mode);
      if (details.exit_reason) parts.push(details.exit_reason);
      if (details.trigger_price !== undefined && details.trigger_price !== null) parts.push(`trigger ${this.$helper.formatPrice(details.trigger_price)}`);
      if (details.leg_name) parts.push(details.leg_name);
      if (details.reason) parts.push(details.reason);
      if (details.expected_price !== undefined) parts.push(`expected ${this.$helper.formatPrice(details.expected_price)}`);
      if (details.slippage_pct !== undefined) parts.push(`slippage ${details.slippage_pct}%`);
      if (details.target_lock_pct !== undefined) parts.push(`locked at ${details.target_lock_pct}%`);
      return parts.length ? parts.join(' · ') : '—';
    },
  },
};
</script>

<style scoped>
.stat-value {
  font-variant-numeric: normal;
}

.mode-toggle {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
}

.mode-btn {
  min-width: 84px;
}

.chart-box {
  height: 320px;
  position: relative;
}

.range-total-row td {
  border-top: 2px solid var(--border) !important;
}

.table-scroll table {
  min-width: 900px;
}

.decision-detail-cell {
  white-space: nowrap;
  color: rgb(var(--v-theme-on-surface));
  opacity: 0.7;
  font-size: 0.8125rem;
}

.timeline-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.timeline-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.timeline-index {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(var(--v-theme-primary), 0.12);
  color: rgb(var(--v-theme-primary));
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.timeline-body {
  flex: 1 1 auto;
}
</style>
